import { pageHead } from "@app/framework/meta";
import { URLS } from "@app/shared";
import { PageHeader, type BreadcrumbItem } from "@design-system/components";
import { trans } from "@mongez/localization";
import type { LoaderFunction, MetaFunction } from "@remix-run/node";
import { json } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";

export const loader: LoaderFunction = async ({ params }) => {
  return json({});
};

export default function CookiePolicy() {
  const data = useLoaderData<typeof loader>();

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: trans("home"), link: URLS.home },
    { label: trans("privacyPolicy") },
  ];

  return (
    <>
      <PageHeader
        translationKey="privacyPolicy"
        breadcrumbItems={breadcrumbItems}
      >
        
      </PageHeader>

      <div className="container mx-auto px-4 py-8">
        <div className="prose prose-lg max-w-none mx-auto">

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{trans("cookiePolicy.typesOfCookies.title")}</h2>
            
            <div className="grid gap-6 md:grid-cols-2">
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-green-800 mb-3">{trans("cookiePolicy.typesOfCookies.essential.title")}</h3>
                <p className="text-green-700 mb-3">
                  {trans("cookiePolicy.typesOfCookies.essential.description")}
                </p>
                <div className="text-sm text-green-600">
                  <strong>{trans("cookiePolicy.typesOfCookies.essential.status")}:</strong> {trans("cookiePolicy.typesOfCookies.essential.note")}
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-blue-800 mb-3">{trans("cookiePolicy.typesOfCookies.analytics.title")}</h3>
                <p className="text-blue-700 mb-3">
                  {trans("cookiePolicy.typesOfCookies.analytics.description")}
                </p>
                <div className="text-sm text-blue-600">
                  <strong>{trans("cookiePolicy.typesOfCookies.analytics.status")}:</strong> {trans("cookiePolicy.typesOfCookies.analytics.note")}
                </div>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-purple-800 mb-3">{trans("cookiePolicy.typesOfCookies.functional.title")}</h3>
                <p className="text-purple-700 mb-3">
                  {trans("cookiePolicy.typesOfCookies.functional.description")}
                </p>
                <div className="text-sm text-purple-600">
                  <strong>{trans("cookiePolicy.typesOfCookies.functional.status")}:</strong> {trans("cookiePolicy.typesOfCookies.functional.note")}
                </div>
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-orange-800 mb-3">{trans("cookiePolicy.typesOfCookies.marketing.title")}</h3>
                <p className="text-orange-700 mb-3">
                  {trans("cookiePolicy.typesOfCookies.marketing.description")}
                </p>
                <div className="text-sm text-orange-600">
                  <strong>{trans("cookiePolicy.typesOfCookies.marketing.status")}:</strong> {trans("cookiePolicy.typesOfCookies.marketing.note")}
                </div>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{trans("cookiePolicy.specificCookies.title")}</h2>
            
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {trans("cookiePolicy.specificCookies.table.cookieName")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {trans("cookiePolicy.specificCookies.table.purpose")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {trans("cookiePolicy.specificCookies.table.type")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {trans("cookiePolicy.specificCookies.table.duration")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      _ga
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {trans("cookiePolicy.specificCookies.table.ga.purpose")}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                        {trans("cookiePolicy.specificCookies.table.ga.type")}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {trans("cookiePolicy.specificCookies.table.ga.duration")}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      _ga_*
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {trans("cookiePolicy.specificCookies.table.gaSession.purpose")}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                        {trans("cookiePolicy.specificCookies.table.gaSession.type")}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {trans("cookiePolicy.specificCookies.table.gaSession.duration")}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      cookie_consent
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {trans("cookiePolicy.specificCookies.table.consent.purpose")}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                        {trans("cookiePolicy.specificCookies.table.consent.type")}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {trans("cookiePolicy.specificCookies.table.consent.duration")}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      language_preference
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {trans("cookiePolicy.specificCookies.table.language.purpose")}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                        {trans("cookiePolicy.specificCookies.table.language.type")}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {trans("cookiePolicy.specificCookies.table.language.duration")}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{trans("cookiePolicy.managingPreferences.title")}</h2>
            <p className="text-gray-700 mb-4">
              {trans("cookiePolicy.managingPreferences.description")}
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
              <li>{trans("cookiePolicy.managingPreferences.methods.banner")}</li>
              <li>{trans("cookiePolicy.managingPreferences.methods.footer")}</li>
              <li>{trans("cookiePolicy.managingPreferences.methods.browser")}</li>
            </ul>
            <p className="text-gray-700 mb-4">
              {trans("cookiePolicy.managingPreferences.note")}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{trans("cookiePolicy.pdplRights.title")}</h2>
            <p className="text-gray-700 mb-4">
              {trans("cookiePolicy.pdplRights.description")}
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
              <li><strong>{trans("cookiePolicy.pdplRights.rights.informed")}</strong></li>
              <li><strong>{trans("cookiePolicy.pdplRights.rights.consent")}</strong></li>
              <li><strong>{trans("cookiePolicy.pdplRights.rights.withdraw")}</strong></li>
              <li><strong>{trans("cookiePolicy.pdplRights.rights.access")}</strong></li>
              <li><strong>{trans("cookiePolicy.pdplRights.rights.rectification")}</strong></li>
              <li><strong>{trans("cookiePolicy.pdplRights.rights.erasure")}</strong></li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{trans("cookiePolicy.thirdPartyServices.title")}</h2>
            <p className="text-gray-700 mb-4">
              {trans("cookiePolicy.thirdPartyServices.description")}
            </p>
            <p className="text-gray-700">
              {trans("cookiePolicy.thirdPartyServices.googleAnalytics")}{" "}
              <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" 
                 className="text-blue-600 hover:text-blue-800 underline">
                {trans("cookiePolicy.thirdPartyServices.googleOptOut")}
              </a>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{trans("cookiePolicy.updates.title")}</h2>
            <p className="text-gray-700 mb-4">
              {trans("cookiePolicy.updates.description")}
            </p>
            <p className="text-gray-700">
              {trans("cookiePolicy.updates.encouragement")}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{trans("cookiePolicy.contactUs.title")}</h2>
            <p className="text-gray-700 mb-4">
              {trans("cookiePolicy.contactUs.description")}
            </p>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-700 mb-2">
                <strong>{trans("cookiePolicy.contactUs.email")}</strong>{" "}
                <a href="mailto:privacy@senaat.com" className="text-blue-600 hover:text-blue-800 underline">privacy@senaat.com</a>
              </p>
              <p className="text-gray-700 mb-2">
                <strong>{trans("cookiePolicy.contactUs.phone")}</strong>{" "}
                <a href="tel:+966 13 810 8111" className="text-blue-600 hover:text-blue-800 underline">+966 13 810 8111</a>
              </p>
              <p className="text-gray-700">
                <strong>{trans("cookiePolicy.contactUs.address")}</strong> {trans("cookiePolicy.contactUs.addressValue")}
              </p>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

export const meta: MetaFunction = ({ data }: any) => {
  return pageHead({
    title: data?.metaData?.title || trans("cookiePolicy.title"),
    description: data?.metaData?.description || trans("cookiePolicy.subtitle"),
  });
};